import React, { useState } from 'react';
import { Icon } from '@mercator-gcp/grout';
import PopoverMenu from 'components/elements/PopoverMenu';
import AddProjectToCollectionModal from 'components/widgets/SaveProject/AddProjectToCollectionModal';
import ConfirmHideModal from 'components/widgets/SaveProject/ConfirmHideModal';
import ConfirmUnsaveModal from 'components/widgets/SaveProject/ConfirmUnsaveModal';
import SavedMenuButton from 'components/widgets/SaveProject/SavedMenu/SavedMenuButton';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { useSavedProjects } from 'hooks/useSavedProjects';
import { useToast } from 'hooks/useToast';
import { CategoryEnum, SavedProject } from 'types/SavedProject';
import type { ButtonProps } from '@mercator-gcp/grout';

type TSavedMenuProps = {
  projectSlug?: string;
  documentId?: string;
  savedProject?: SavedProject;
  isUnstyled?: boolean;
  sx?: ButtonProps['sx'];
  showLabel?: boolean;
  isWatched?: boolean;
  isStarred?: boolean;
  isHidden?: boolean;
  isSharedCollection?: boolean;
};

const SavedMenu = ({
  projectSlug,
  documentId,
  savedProject,
  isUnstyled = false,
  sx = {},
  showLabel = true,
  isWatched = false,
  isStarred = false,
  isHidden = false,
  isSharedCollection = false,
}: TSavedMenuProps) => {
  const [isOpenConfirmUnsave, setIsOpenConfirmUnsave] = useState(false);
  const [isOpenConfirmHide, setIsOpenConfirmHide] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { unsaveProject, starProject, watchProject } = useSavedProjects();
  const { hideProject, unhideProject } = useHiddenProjects();
  const thisSavedProject = savedProject;
  const [addToCollectionOpen, setAddToCollectionOpen] = useState(false);
  const [projectCategory, setProjectCategory] = useState('');
  const [pastProjectCategory, setPastProjectCategory] = useState('uncategorized');
  const { renderToast } = useToast();

  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleProjectCategory = (category: CategoryEnum) => {
    setProjectCategory(category);
    if (thisSavedProject?.category) {
      // set the past category
      setPastProjectCategory(thisSavedProject.category);
    }
  };

  const onConfirmUnsave = () => {
    if (thisSavedProject) {
      unsaveProject.mutate({
        collectionId: thisSavedProject.collection,
        projectSlug,
        documentId,
        currentProjectCategory: thisSavedProject.category,
      });
    }
    setIsOpenConfirmUnsave(false);
    closeMenu();
  };

  const onConfirmHide = () => {
    hideProject.mutate({
      projectSlug,
      documentId,
      currentProjectCategory: thisSavedProject?.category || 'uncategorized',
    });
    setIsOpenConfirmHide(false);
    closeMenu();
  };

  const handleClickClear = () => {
    if (isHidden) {
      if (projectSlug) {
        unhideProject.mutate(projectSlug);
        renderToast({
          toastTitle: 'Unhide project',
          label: 'Project was unhidden',
        });
      }
    } else {
      setIsOpenConfirmUnsave(true);
    }
  };

  const handleClickStar = () => {
    handleProjectCategory(CategoryEnum.STAR);
    if (isHidden) {
      setAddToCollectionOpen(true);
    } else if (thisSavedProject) {
      starProject.mutate({
        collectionId: thisSavedProject.collection,
        projectSlug,
        documentId,
        currentProjectCategory: thisSavedProject.category,
        isSharedCollection,
      });
    }
    closeMenu();
  };

  const handleClickWatch = () => {
    handleProjectCategory(CategoryEnum.WATCH);
    if (isHidden) {
      setAddToCollectionOpen(true);
    } else if (thisSavedProject) {
      watchProject.mutate({
        collectionId: thisSavedProject.collection,
        projectSlug,
        documentId,
        currentProjectCategory: thisSavedProject.category,
        isSharedCollection,
      });
    }
    closeMenu();
  };

  return (
    <>
      {addToCollectionOpen && (
        <AddProjectToCollectionModal
          isOpen={addToCollectionOpen}
          onClose={() => setAddToCollectionOpen(false)}
          projectCategory={projectCategory}
          currentProjectCategory={pastProjectCategory}
          projectSlug={projectSlug}
          documentId={documentId}
          isUnhidingProject
        />
      )}
      {isOpenConfirmUnsave && (
        <ConfirmUnsaveModal
          isOpen={isOpenConfirmUnsave}
          onClose={() => setIsOpenConfirmUnsave(false)}
          onConfirm={onConfirmUnsave}
        />
      )}
      {isOpenConfirmHide && (
        <ConfirmHideModal
          isOpen={isOpenConfirmHide}
          onClose={() => setIsOpenConfirmHide(false)}
          onConfirm={onConfirmHide}
        />
      )}
      <PopoverMenu isOpen={isMenuOpen} onClose={closeMenu}>
        <PopoverMenu.Trigger>
          <SavedMenuButton
            isUnstyled={isUnstyled}
            sx={sx}
            showLabel={showLabel}
            isWatched={isWatched}
            isStarred={isStarred}
            isHidden={isHidden}
            onClick={onButtonClick}
          />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content>
          <PopoverMenu.Header label="save to lead list" />
          <PopoverMenu.Item
            onClick={handleClickStar}
            iconName={Icon.Name.Star}
            label="Qualify"
            isDisabled={isStarred}
          />
          <PopoverMenu.Item
            onClick={handleClickWatch}
            iconName={Icon.Name.Eye}
            label="Monitor"
            isDisabled={isWatched}
          />
          {!isSharedCollection && (
            <>
              <PopoverMenu.Divider />
              <PopoverMenu.Item
                onClick={() => setIsOpenConfirmHide(true)}
                iconName={Icon.Name.EyeHide}
                label="Pass"
                isDisabled={isHidden}
              />
              <PopoverMenu.Divider />
              <PopoverMenu.Item onClick={handleClickClear} label="Clear" />
            </>
          )}
        </PopoverMenu.Content>
      </PopoverMenu>
    </>
  );
};

export default SavedMenu;
